import React, { useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import * as XLSX from 'xlsx';

interface ExcelData {
  /** Sheet name */
  sheetName?: string;
  /** Data to export - can be array of objects, 2D array, or HTML table selector */
  data: any[][] | Array<Record<string, any>> | string;
  /** Optional headers if data is 2D array */
  headers?: string[];
}

interface DataExtractor {
  /** Type of data extraction */
  type: 'chart' | 'list' | 'key-value' | 'custom';
  /** Data selector or extraction function */
  selector?: string | ((element: HTMLElement) => ExcelData[]);
  /** Optional transformation function for extracted data */
  transform?: (data: any) => any[][] | Array<Record<string, any>>;
}

export interface LomavisDownloadExcelElementProps {
  /** Data to export to Excel - single sheet or multiple sheets */
  excelData?: ExcelData | ExcelData[];
  /** Optional filename for the download */
  fileName?: string;
  /** Optional function to override the download behavior */
  onDownload?: () => Promise<void>;
  /** Optional ref to DOM element to extract table data from */
  elementRef?: React.RefObject<HTMLElement>;
  /** Data extraction configuration for non-table elements */
  dataExtractor?: DataExtractor;
  /** Static data to include (useful for mixed content) */
  staticData?: Array<Record<string, any>>;
}

/**
 * Atoms: LomavisDownloadExcelElement
 *
 * A component that downloads content as Excel (.xlsx) file.
 * Supports multiple data sources: objects, arrays, and HTML tables.
 */
export const LomavisDownloadExcelElement: React.FC<
  LomavisDownloadExcelElementProps
> = ({
  excelData,
  fileName = 'export',
  onDownload,
  elementRef,
  dataExtractor,
  staticData
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const sanitizeFileName = (name: string): string => {
    return name
      .trim()
      .replace(/[^\w\s-]/g, '') // Remove special chars except spaces and hyphens
      .replace(/\s+/g, '_') // Replace spaces with underscores
      .replace(/_+/g, '_') // Collapse multiple underscores
      .replace(/^_+|_+$/g, ''); // Trim leading/trailing underscores
  };

  const extractDataFromElement = (element: HTMLElement): ExcelData[] => {
    // If dataExtractor is provided, use it
    if (dataExtractor) {
      if (typeof dataExtractor.selector === 'function') {
        return dataExtractor.selector(element);
      }

      // Handle different extraction types
      switch (dataExtractor.type) {
        case 'chart':
          return extractChartData(element, dataExtractor);
        case 'list':
          return extractListData(element, dataExtractor);
        case 'key-value':
          return extractKeyValueData(element, dataExtractor);
        case 'custom':
          // Custom extraction logic can be provided
          return [];
      }
    }

    // Fallback to table extraction
    return extractTableData(element);
  };

  const extractChartData = (
    element: HTMLElement,
    extractor: DataExtractor
  ): ExcelData[] => {
    // Look for chart containers or canvas elements
    const chartData: ExcelData[] = [];

    // Look for data attributes or text content that might contain chart data
    const dataElements = element.querySelectorAll(
      '[data-chart-data], .chart-legend, .chart-labels'
    );

    if (dataElements.length > 0) {
      const extractedData: string[][] = [['Category', 'Value', 'Label']];

      dataElements.forEach((el) => {
        const dataAttr = el.getAttribute('data-chart-data');
        if (dataAttr) {
          try {
            const parsedData = JSON.parse(dataAttr);
            if (Array.isArray(parsedData)) {
              parsedData.forEach((item: any) => {
                extractedData.push([
                  item.label || item.name || 'Unknown',
                  item.value || item.y || item.data || 0,
                  item.category || item.series || ''
                ]);
              });
            }
          } catch (e) {
            console.warn('Could not parse chart data:', e);
          }
        } else {
          // Extract text content as fallback
          const text = el.textContent?.trim();
          if (text) {
            extractedData.push([text, '', '']);
          }
        }
      });

      chartData.push({
        sheetName: 'Chart_Data',
        data: extractedData
      });
    }

    // If extractor has transform function, apply it
    if (extractor.transform && chartData.length > 0) {
      const transformed = extractor.transform(chartData[0].data);
      chartData[0].data = transformed;
    }

    return chartData;
  };

  const extractListData = (
    element: HTMLElement,
    extractor: DataExtractor
  ): ExcelData[] => {
    const selector =
      typeof extractor.selector === 'string'
        ? extractor.selector
        : 'li, .list-item, .item';
    const items = element.querySelectorAll(selector);

    const listData: string[][] = [['Item', 'Value', 'Description']];

    items.forEach((item, index) => {
      // Try to extract structured data from item
      const title =
        item
          .querySelector('.title, .name, h1, h2, h3, h4, h5, h6')
          ?.textContent?.trim() || `Item ${index + 1}`;
      const value =
        item
          .querySelector('.value, .amount, .price, .count, [data-value]')
          ?.textContent?.trim() || '';
      const description =
        item.querySelector('.description, .subtitle, p')?.textContent?.trim() ||
        item.textContent?.trim() ||
        '';

      listData.push([title, value, description]);
    });

    const result: ExcelData[] = [
      {
        sheetName: 'List_Data',
        data: extractor.transform ? extractor.transform(listData) : listData
      }
    ];

    return result;
  };

  const extractKeyValueData = (
    element: HTMLElement,
    extractor: DataExtractor
  ): ExcelData[] => {
    const selector =
      typeof extractor.selector === 'string'
        ? extractor.selector
        : '.key-value, .property, .stat';
    const pairs = element.querySelectorAll(selector);

    const kvData: string[][] = [['Property', 'Value']];

    pairs.forEach((pair) => {
      // Look for key-value patterns
      const key = pair
        .querySelector('.key, .label, .property-name, dt')
        ?.textContent?.trim();
      const value = pair
        .querySelector('.value, .amount, .property-value, dd')
        ?.textContent?.trim();

      if (key && value) {
        kvData.push([key, value]);
      } else {
        // Fallback: split text content by colon or other separators
        const text = pair.textContent?.trim();
        if (text?.includes(':')) {
          const [k, v] = text.split(':').map((s) => s.trim());
          kvData.push([k || '', v || '']);
        }
      }
    });

    const result: ExcelData[] = [
      {
        sheetName: 'Key_Value_Data',
        data: extractor.transform ? extractor.transform(kvData) : kvData
      }
    ];

    return result;
  };

  const createWorkbook = (dataArray: ExcelData[]): XLSX.WorkBook => {
    const wb = XLSX.utils.book_new();

    dataArray.forEach((sheetData, index) => {
      let ws: XLSX.WorkSheet;
      const sheetName = sheetData.sheetName || `Sheet${index + 1}`;

      if (typeof sheetData.data === 'string') {
        // If data is a CSS selector, try to find table in document
        const table = document.querySelector<HTMLTableElement>(sheetData.data);
        if (table) {
          ws = XLSX.utils.table_to_sheet(table);
        } else {
          // Fallback: create empty sheet with error message
          ws = XLSX.utils.aoa_to_sheet([
            ['Error: Table not found with selector:', sheetData.data]
          ]);
        }
      } else if (Array.isArray(sheetData.data)) {
        if (sheetData.data.length > 0 && Array.isArray(sheetData.data[0])) {
          // 2D array
          const arrayData = sheetData.data as any[][];
          if (sheetData.headers) {
            arrayData.unshift(sheetData.headers);
          }
          ws = XLSX.utils.aoa_to_sheet(arrayData);
        } else {
          // Array of objects
          ws = XLSX.utils.json_to_sheet(
            sheetData.data as Array<Record<string, any>>
          );
        }
      } else {
        // Single object - convert to array
        ws = XLSX.utils.json_to_sheet([sheetData.data]);
      }

      XLSX.utils.book_append_sheet(wb, ws, sheetName);
    });

    return wb;
  };

  const extractTableData = (element: HTMLElement): ExcelData[] => {
    const tables = element.querySelectorAll('table');
    const tableData: ExcelData[] = [];

    tables.forEach((table, index) => {
      const rows = Array.from(table.querySelectorAll('tr'));
      const data: Array<Array<string | number>> = [];

      rows.forEach((row) => {
        const cells = Array.from(row.querySelectorAll('th, td'));
        const rowData = cells.map((cell) => {
          // First, check if the cell has data attributes for the original value
          const dataValue =
            cell.getAttribute('data-original-value') ||
            cell.getAttribute('data-value') ||
            cell.getAttribute('data-raw-value');

          if (dataValue !== null) {
            // Try to parse as number if it looks like one
            const numValue = parseFloat(dataValue);
            return isNaN(numValue) ? dataValue : numValue;
          }

          // Check if there's a LomavisShortenedNumber component or similar
          const shortenedNumberEl = cell.querySelector(
            '[data-full-value], [data-original-value]'
          );
          if (shortenedNumberEl) {
            const fullValue =
              shortenedNumberEl.getAttribute('data-full-value') ||
              shortenedNumberEl.getAttribute('data-original-value');
            if (fullValue !== null) {
              const numValue = parseFloat(fullValue);
              return isNaN(numValue) ? fullValue : numValue;
            }
          }

          // Look for any element with numeric data attributes
          const numericElement = cell.querySelector('[data-numeric-value]');
          if (numericElement) {
            const numericValue =
              numericElement.getAttribute('data-numeric-value');
            if (numericValue !== null) {
              const numValue = parseFloat(numericValue);
              return isNaN(numValue) ? numericValue : numValue;
            }
          }

          // Check if the text content represents a shortened number (K, M, B suffixes)
          const textContent = cell.textContent?.trim() || '';

          // Fallback to text content
          return textContent;
        });
        data.push(rowData);
      });

      if (data.length > 0) {
        tableData.push({
          sheetName: `Table_${index + 1}`,
          data
        });
      }
    });

    return tableData;
  };

  const defaultDownload = async (): Promise<void> => {
    let dataToExport: ExcelData[] = [];

    // Priority order: staticData -> excelData -> element extraction
    if (staticData) {
      dataToExport.push({
        sheetName: 'Static_Data',
        data: staticData
      });
    }

    if (excelData) {
      const providedData = Array.isArray(excelData) ? excelData : [excelData];
      dataToExport = [...dataToExport, ...providedData];
    }

    if (elementRef?.current) {
      // Extract data from DOM element using specified extractor or default table extraction
      const extractedData = extractDataFromElement(elementRef.current);
      if (extractedData.length > 0) {
        dataToExport = [...dataToExport, ...extractedData];
      }
    }

    // Ensure we have some data to export
    if (dataToExport.length === 0) {
      console.warn('No data available for export');
      dataToExport = [
        {
          sheetName: 'NoData',
          data: [['No data available for export']]
        }
      ];
    }

    // Create workbook
    const wb = createWorkbook(dataToExport);

    // Generate file and trigger download
    const sanitizedFileName = sanitizeFileName(fileName);
    XLSX.writeFile(wb, `${sanitizedFileName}.xlsx`);
  };

  const handleClick = async (): Promise<void> => {
    setIsLoading(true);
    try {
      if (onDownload) {
        await onDownload();
      } else {
        await defaultDownload();
      }
    } catch (err) {
      console.error('Excel download failed:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Button
      variant={'light'}
      className="bg-white btn-white"
      size={'sm'}
      data-html2canvas-ignore="true"
      onClick={() => {
        void handleClick();
      }}
      disabled={isLoading}
    >
      {isLoading ? (
        <Spinner animation="border" size="sm" className="me-1" />
      ) : (
        <i className="fa-regular fa-file-spreadsheet fs-4" />
      )}
    </Button>
  );
};
